/**
 * StudioGenerating — interstitial loader (Phase A — charte split #0F2B5B / #4FC3F7).
 * ~8 seconds of artificial progress with specialty-aware sequential steps, then
 * auto-redirects to /studio/preview/:id?mode=demo.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Circle, HeartPulse } from "lucide-react";

const TOTAL_MS = 8000;

const SPECIALTY_STEPS = {
  "Médecin": [
    "Identité Pro Santé Connect configurée",
    "Activation ordonnance numérique",
    "Connexion DMP & MSSanté webmail",
    "Agenda intelligent + créneaux téléconsultation",
  ],
  "Dermato": [
    "Identité Pro Santé Connect configurée",
    "Connexion dermatoscope numérique",
    "Module téléexpertise image (CDA-R2 N1)",
    "Réseau de dermatologues du territoire",
  ],
  "Ophtalmo": [
    "Identité Pro Santé Connect configurée",
    "Activation téléconsultation assistée",
    "Connexion OCT + rétinographie",
    "Conformité ANS · HDS · RGPD vérifiée",
  ],
  "CPTS": [
    "Identité Pro Santé Connect configurée",
    "Création espace multi-PS",
    "Tableau de bord collectif activé",
    "Réseau de proximité territorial",
  ],
  "Kiné": [
    "Identité Pro Santé Connect configurée",
    "Module télésurveillance activé",
    "Agenda + comptes-rendus automatisés",
    "Conformité ANS · HDS · RGPD vérifiée",
  ],
  "Infirmier": [
    "Identité Pro Santé Connect configurée",
    "Module télésurveillance IDEL activé",
    "Agenda + comptes-rendus automatisés",
    "Conformité ANS · HDS · RGPD vérifiée",
  ],
};

export default function StudioGenerating() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const specialty = sp.get("specialite") || "Médecin";
  const brandName = sp.get("name") || "votre cabinet";
  const navigate = useNavigate();
  const steps = useMemo(() => SPECIALTY_STEPS[specialty] || SPECIALTY_STEPS["Médecin"], [specialty]);
  const [progress, setProgress] = useState(0);
  const [stepsDone, setStepsDone] = useState(0);
  const [remaining, setRemaining] = useState(8);

  useEffect(() => {
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const elapsed = t - start;
      const p = Math.min(1, elapsed / TOTAL_MS);
      setProgress(Math.round(p * 100));
      setRemaining(Math.max(0, Math.ceil((TOTAL_MS - elapsed) / 1000)));
      if (p < 1) raf = requestAnimationFrame(tick);
      else navigate(`/studio/preview/${id}?mode=demo&quickstart=1`, { replace: true });
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [id, navigate]);

  useEffect(() => {
    const ivl = setInterval(() => setStepsDone((s) => Math.min(steps.length, s + 1)), 1600);
    return () => clearInterval(ivl);
  }, [steps.length]);

  return (
    <div
      data-testid="studio-generating"
      className="fixed inset-0 z-[200] flex flex-col items-center justify-center p-6"
      style={{ background: "#0F2B5B" }}
    >
      {/* Logo block */}
      <div
        className="grid place-items-center mb-7"
        style={{
          width: 64, height: 64,
          background: "rgba(255,255,255,0.1)",
          borderRadius: 12,
        }}
      >
        <HeartPulse size={32} style={{ color: "#4FC3F7" }} />
      </div>

      <h1
        data-testid="generating-title"
        className="font-display"
        style={{ color: "#ffffff", fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}
      >
        Génération de votre plateforme
      </h1>
      <p className="mt-2" style={{ color: "#93B4D4", fontSize: 14 }}>
        Cabinet <span style={{ color: "#ffffff", fontWeight: 500 }}>{brandName}</span>
        <span className="mx-1.5" style={{ color: "rgba(147,180,212,0.55)" }}>·</span>
        <span style={{ color: "#ffffff" }}>{specialty}</span>
      </p>

      {/* Progress bar */}
      <div
        className="mt-6 overflow-hidden"
        data-testid="generating-bar"
        style={{
          width: 400, maxWidth: "92vw", height: 6,
          background: "rgba(255,255,255,0.1)",
          borderRadius: 20,
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: "100%",
            background: "#4FC3F7",
            borderRadius: 20,
            transition: "width 300ms ease-out",
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between" style={{ width: 400, maxWidth: "92vw" }}>
        <span data-testid="generating-remaining" style={{ color: "#4FC3F7", fontSize: 13 }}>
          Votre plateforme sera prête dans <strong style={{ color: "#ffffff" }}>{remaining}</strong> secondes
        </span>
        <span data-testid="generating-percent" style={{ color: "#93B4D4", fontSize: 13, fontVariantNumeric: "tabular-nums" }}>{progress}%</span>
      </div>

      {/* Step list */}
      <div className="mt-8 space-y-3" data-testid="generating-steps" style={{ width: 400, maxWidth: "92vw" }}>
        {steps.map((label, i) => {
          const done = i < stepsDone;
          const current = i === stepsDone;
          return (
            <div key={label} className="flex items-center gap-3">
              {done ? (
                <CheckCircle2 size={18} style={{ color: "#4FC3F7", flexShrink: 0 }} />
              ) : current ? (
                <Loader2 size={18} className="animate-spin" style={{ color: "#ffffff", flexShrink: 0 }} />
              ) : (
                <Circle size={18} style={{ color: "#475569", flexShrink: 0 }} />
              )}
              <span
                style={{
                  fontSize: 14,
                  color: done ? "#B8D4E8" : current ? "#ffffff" : "#475569",
                  fontWeight: current ? 500 : 400,
                }}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
